<script setup lang="ts">
import { ref, computed } from "vue";

import FileRedactionMessage from "@/components/FileRedactionMessage.vue";
import DocumentViewerFile from "./DocumentViewer/DocumentViewerFile.vue";
import VoiceTranscriptFile from "./VoiceTranscript/VoiceTranscriptFile.vue";

import { isItemBurning, isShowingFile } from "@/services/UtilitiesService";

import { useMetadataStore } from "@/stores/metadata-store";
import { useCaseFileStore } from "@/stores/case-file-store";
import { useAnnotationStore } from "@/stores/annotation-store";

import { useWindow } from "@/hooks/use-window";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import type { HighlightCustomArea } from "@/models/Viewer";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

withDefaults(defineProps<DockviewPanelComponentProps<false>>(), {  })
defineEmits<DockviewPanelComponentEmits>();

const window = useWindow();
const routeParams = useCaseRouteParams();

const selectedArea = ref<{ fileReference: string; area: HighlightCustomArea }>();
const fallbackSize = ref({
  width: window.value.innerWidth,
  height: window.value.innerHeight - 60,
});

const cachedFiles = computed(() => {
  const filesByRef = useMetadataStore().filesByRef;

  return useCaseFileStore().cachedFiles.flatMap((c) => {
    const file = filesByRef[c.fileReference];
    if (file == null) return [];

    return {
      file,
      type: file.fileType === "audio" || file.fileType === "ac5" ? ("transcript" as const) : ("document" as const),
    };
  });
});

// It is used
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getProps(isShowing: boolean) {
  return {
    isPopout: false,
    routeParams: routeParams.value,
    scale: __props.dockview?.size ?? fallbackSize.value,
    isVisible: (__props.dockview?.isVisible ?? true) && isShowing,
  };
}

useAnnotationStore().$onAction(({ name, args }) => {
  if (name !== ("selectArea" satisfies typeof name)) return;
  selectedArea.value = { fileReference: args[1], area: args[0] };
});
</script>
<template>
  <div v-if="cachedFiles.length < 1 && routeParams.casefile == null" class="tw-grid tw-size-full tw-place-items-center">
    <div class="tw-max-w-sm tw-text-pretty">No file selected</div>
  </div>
  <template v-for="{ file, type } in cachedFiles" v-else :key="file.fileReference">
    <template v-for="(isShowing, idx) in [isShowingFile(file)]" :key="idx">
      <FileRedactionMessage
        v-if="isShowing && isItemBurning(file)"
        class="tw-absolute tw-left-0 tw-top-0 tw-flex tw-size-full tw-items-center tw-justify-center tw-p-4"
        :file="file"
        :route-params="routeParams"
      />
      <VoiceTranscriptFile
        v-else-if="type === 'transcript'"
        v-show="isShowing"
        :file="file"
        :="getProps(isShowing)"
        @panel-popout-action="(arg) => $emit('panelPopoutAction', arg)"
      />
      <DocumentViewerFile
        v-else
        v-show="isShowing"
        :file="file"
        :="getProps(isShowing)"
        :selected-area="selectedArea?.fileReference === file.fileReference ? selectedArea.area : undefined"
        @panel-popout-action="(arg) => $emit('panelPopoutAction', arg)"
      />
    </template>
  </template>
</template>
